import React, { useEffect, useMemo, useState } from "react";
import PropertySelect from "./PropertySelect";
import SelectedLabel from "./SelectedLabel";
import { IndividualOption, PropertyState } from "./Step3.types";
import RecipientItem from "../CampaignSteps/components/RecipientItem";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";
import { getUsersByProperties } from "PhishingManager/service/index.Service";
import DebounceInput from "PhishingManager/components/DebouncedInput";
import { useTranslation } from "react-i18next";


interface Props {
    loading: boolean;
    tenentId: number;
    propertyData: PropertyState;
    selectedProperties: number[];
    allPropertiesSelected: boolean;
    selectedIndividuals: IndividualOption[];
    setSelectedIndividuals: (individuals: IndividualOption[]) => void;
    toggleLoading: (status: boolean) => void;
    setSelectedProperties: (propertyIds: number[]) => void;
    setPropertySearchQuery: (val: string | null) => void;
    moveCurrentPage: () => void;
    setAllPropertiesSelected: (val: boolean) => void;
    setSelectedUserCount: (value: number) => void;
}

type INDIVIDUAL_STATE = {
    search: string,
    data: IndividualOption[],
    currentPage: number,
    totalPages: number,
    renderKey: number
}

const initState = {
    search: "",
    data: [],
    currentPage: 1,
    totalPages: 0,
    renderKey: 0
}

const IndividualRecipients: React.FC<Props> = ({
    tenentId,
    loading,
    propertyData,
    selectedProperties,
    selectedIndividuals,
    allPropertiesSelected,
    toggleLoading,
    setSelectedProperties,
    setSelectedIndividuals,
    setPropertySearchQuery,
    moveCurrentPage,
    setAllPropertiesSelected,
    setSelectedUserCount
}) => {
    const [individualState, setIndividualState] = useState<INDIVIDUAL_STATE>({...initState});
    const totalUsersCount = selectedIndividuals.length;
    const { t } = useTranslation();

    const allIndividualsSelected = useMemo(() => {
        return individualState.data.length ? individualState.data.length === selectedIndividuals.length : false;
    }, [individualState.data, selectedIndividuals])

    const handleSelectAllIndividuals = () => {
        let data : IndividualOption[] = [];
        if(!allIndividualsSelected)  data = individualState.data;
        setSelectedIndividuals(data);
    }

    const handleIndividualToggle = (user: IndividualOption) => {
        const isSelected = selectedIndividuals.some((g) => g.value === user.value);
        setSelectedIndividuals(isSelected ? selectedIndividuals.filter((g) => g.value !== user.value) : [...selectedIndividuals, user]);
    };
    
    const resetRenderKey = (obj?: Partial<INDIVIDUAL_STATE>) => {
        const newState = obj || {};
        const element = document.querySelector('#individual-list-infinite-scroll');
        let isPageChange = false;
        if (Object.keys(newState).includes("currentPage")) isPageChange = true;
        if(!isPageChange && element) element.scrollTo({ top: 0, behavior: 'smooth' });

        setIndividualState(prev => ({ ...prev, ...newState, currentPage: (isPageChange ? prev.currentPage + 1 : 1), renderKey: Math.random() + 999 }));
    }

    const getData = async () => {
        try {
            if (!tenentId || !selectedProperties.length) return;
            const { currentPage, search } = individualState;
            toggleLoading(true);
            const res = await getUsersByProperties(Number(tenentId), selectedProperties, search, currentPage, 25, allPropertiesSelected);
            const users = (res?.data || []).map((user: any) => ({
                value: user.id,
                label: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                email: user.email,
                propertyId: user.property_id || 0
            }));
            setIndividualState(prev => {
                const baseData = currentPage === 1 ? users : [...prev.data, ...users];
                return {
                    ...prev,
                    data: baseData,
                    totalPages: res?.meta?.total || 0
                };
            });
        } catch (error) {
            setIndividualState(prev => ({...prev, currentPage: 1, data: [], totalPages: 0 }));
        } finally {
            toggleLoading(false);
        }
    }

    useEffect(() => {
        if(!selectedProperties.length) {
            setSelectedIndividuals([]);
            setIndividualState({...initState});
        }
    }, [selectedProperties])

    useEffect(() => {
        getData();
    }, [individualState.renderKey])

    useEffect(() => {
        setSelectedUserCount(totalUsersCount);
    }, [totalUsersCount])

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                    {t('phishingManager.campaignSetup.step3.individual.selectProperty')} <span className="required">*</span>
                    </div>
                    <PropertySelect {...{ t, loading, propertyData, selectedProperties, allSelected: allPropertiesSelected, moveCurrentPage, setSearchValue: setPropertySearchQuery }}
                        setSelectedProperties={(propertyIds) => {
                            setSelectedProperties(propertyIds);
                            resetRenderKey();
                        }}
                        setAllSelected={(val) => {
                            setAllPropertiesSelected(val);
                            resetRenderKey();
                        }}
                        onPropertyDeselect={(propertyValue) => {
                            setSelectedIndividuals(
                                selectedIndividuals.filter((group) => {
                                    const selGroup = individualState.data.find((g) => g.value === group.value);
                                    return selGroup?.propertyId !== propertyValue;
                                })
                            );
                            resetRenderKey();
                        }}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <DebounceInput placeholder={t('phishingManager.campaignSetup.step3.individual.searchPlaceholder')} debounceSearch={individualState.search} setDebouncedSearch={(value) => resetRenderKey({ search: value })} />
                    </div>
                </div>
            </div>

            <SelectedLabel t={t} labelText={`${t("phishingManager.campaignSetup.step3.individual.selectIndividuals")} ${individualState.data.length > 1 ? t("phishingManager.campaignSetup.step3.individual.results", { count: individualState.data.length }
            ) : ""}`} totalCount={totalUsersCount} selectedAll={allIndividualsSelected} handleSelectAll={handleSelectAllIndividuals} />

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 && !allPropertiesSelected ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        {t('phishingManager.campaignSetup.step3.individual.selectPropertyMessage')}
                    </div>
                ) : individualState.data.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        {t('phishingManager.campaignSetup.step3.individual.noIndividualsFound')}
                    </div>
                ) : (
                    <InfinityScrollWrapper
                        dataLength={individualState.data.length}
                        hasMore={individualState.data.length < individualState.totalPages}
                        endMessageText=""
                        onLoadMore={() => resetRenderKey({ currentPage: 0 })}
                        loading={loading}
                        height={400}
                        scrollableId="individual-list-infinite-scroll"
                        loaderText={t('phishingManager.campaignSetup.step3.individual.loading')}
                    >
                        {individualState.data.map((individual) => (
                            <RecipientItem
                                key={individual.value}
                                title={individual.label}
                                description={individual.email}
                                iconType="recipientIndividual"
                                isChecked={selectedIndividuals.some(g => g.value === individual.value)}
                                onToggle={() => handleIndividualToggle(individual)}
                            />
                        ))}
                    </InfinityScrollWrapper>
                )}
            </div>
        </div>
    );
};

export default IndividualRecipients;